import { isEmpty, kebabCase, lowerCase, upperCase } from 'lodash';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { Honorific } from '../types/api/graphql';
import { getAge } from './date.utils';

export const isEmptyOrNone = (value: string | number | null | undefined) => {
  if (isEmpty(value) || value === '-') {
    return true;
  }

  return false;
};

export function useFormatters() {
  const intl = useIntl();

  const getInitials = useCallback(
    (
      value:
        | {
            firstName?: string | null | undefined;
            lastName?: string | null | undefined;
          }
        | null
        | undefined
    ): string => {
      if (value) {
        const name = [value.firstName, value.lastName]
          .filter(option => !isEmpty(option))
          .join(' ');

        return name
          .split(' ')
          .map(option => upperCase(option.charAt(0)))
          .join('');
      }

      return '';
    },
    []
  );

  const getFullNameAndHonorific = useCallback(
    (
      value:
        | {
            firstName?: string | null | undefined;
            lastName?: string | null | undefined;
            honorific?: string | Honorific | null | undefined;
          }
        | null
        | undefined
    ): string => {
      if (value) {
        const honorific = value.honorific
          ? intl
              .formatMessage({
                id: `common.honorific.${kebabCase(value.honorific)}`,
              })
              .toLowerCase()
          : '';
        return `${[value.firstName, value.lastName]
          .filter(option => !isEmpty(option))
          .join(' ')}${honorific ? ` (${honorific})` : ''}`;
      }

      return '';
    },
    [intl]
  );

  const getFullName = useCallback(
    (
      value:
        | {
            firstName?: string | null | undefined;
            lastName?: string | null | undefined;
          }
        | null
        | undefined
    ): string => {
      if (value) {
        return [value.firstName, value.lastName]
          .filter(option => !isEmpty(option))
          .join(' ');
      }

      return '';
    },
    []
  );

  const getBirthDate = useCallback(
    (
      value:
        | {
            birthDate?: string | null | undefined;
            gender?: string | null | undefined;
          }
        | null
        | undefined
    ) => {
      if (value?.birthDate) {
        return intl.formatMessage(
          {
            id: 'common.birthdate-placeholder',
          },
          {
            date: intl.formatDate(value.birthDate, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
            age: `(${
              value.gender
                ? intl
                    .formatMessage({
                      id: `common.gender.${lowerCase(value.gender)}`,
                    })
                    .charAt(0)
                : ''
            }, ${intl.formatMessage(
              {
                id: `common.age`,
              },
              { age: getAge(value?.birthDate) }
            )})`,
          }
        );
      }
      return '';
    },
    [intl]
  );

  return { getInitials, getFullName, getFullNameAndHonorific, getBirthDate };
}
